/**
 * FuelChecklistItem.js
 *
 * Inline fuel level selector for pre-departure and arrival checklists.
 * NOT a modal - renders inline like a checklist item.
 * Designed for glove-friendly operation with 56px minimum tap targets.
 *
 * Usage:
 * <FuelChecklistItem
 *   label="Departure Fuel Level"
 *   value={fuelLevel}
 *   onChange={handleFuelChange}
 *   required={true}
 * />
 */

import React, { useState, useCallback } from 'react';
import { Fuel, Check, Edit3, ChevronDown, ChevronUp } from 'lucide-react';
import { FUEL_LEVEL_OPTIONS } from '../../../../../config/operationsConfig';

// Exact value unit options
const EXACT_UNITS = [
  { value: 'percent', label: '%', suffix: '%' },
  { value: 'litres', label: 'L', suffix: 'L' },
  { value: 'gallons', label: 'gal', suffix: 'gal' }
];

const FuelChecklistItem = ({
  label = 'Fuel Level',
  value = null,
  onChange,
  required = false,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExact, setShowExact] = useState(false);
  const [exactValue, setExactValue] = useState('');
  const [exactUnit, setExactUnit] = useState('percent');

  // Check if we have a value
  const hasValue = value !== null && value !== undefined;
  const isComplete = hasValue;

  // Get display value
  const getDisplayValue = () => {
    if (!hasValue) return null;
    if (typeof value === 'object') {
      if (value.isExact) {
        const unit = EXACT_UNITS.find(u => u.value === value.unit);
        return `${value.value}${unit?.suffix || '%'}`;
      }
      return `${value.percentage || value.value}%`;
    }
    return `${value}%`;
  };

  // Get percentage for color coding
  const getPercentage = () => {
    if (!hasValue) return 0;
    if (typeof value === 'object') {
      return value.percentage || (value.unit === 'percent' ? value.value : 50);
    }
    return value;
  };

  // Color based on fuel level
  const getLevelColor = (levelValue, isSelected) => {
    if (isSelected) {
      if (levelValue >= 75) return 'bg-green-600 border-green-700 ring-2 ring-green-300';
      if (levelValue >= 50) return 'bg-cyan-600 border-cyan-700 ring-2 ring-cyan-300';
      if (levelValue >= 25) return 'bg-amber-600 border-amber-700 ring-2 ring-amber-300';
      return 'bg-red-600 border-red-700 ring-2 ring-red-300';
    }
    if (levelValue >= 75) return 'bg-green-500 hover:bg-green-600 border-green-600';
    if (levelValue >= 50) return 'bg-cyan-500 hover:bg-cyan-600 border-cyan-600';
    if (levelValue >= 25) return 'bg-amber-500 hover:bg-amber-600 border-amber-600';
    return 'bg-red-500 hover:bg-red-600 border-red-600';
  };

  // Handle quick selection
  const handleQuickSelect = useCallback((level) => {
    onChange?.({
      value: level.value,
      unit: 'percent',
      percentage: level.value,
      isExact: false
    });
    setIsExpanded(false);
    setShowExact(false);
  }, [onChange]);

  // Handle exact value change
  const handleExactValueChange = useCallback((e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setExactValue(val);
    }
  }, []);

  // Handle exact value confirm
  const handleExactConfirm = useCallback(() => {
    const numValue = parseFloat(exactValue);
    if (!isNaN(numValue) && numValue >= 0) {
      if (exactUnit === 'percent' && numValue > 100) return;
      onChange?.({
        value: numValue,
        unit: exactUnit,
        percentage: exactUnit === 'percent' ? numValue : null,
        isExact: true
      });
      setIsExpanded(false);
      setShowExact(false);
      setExactValue('');
    }
  }, [exactValue, exactUnit, onChange]);

  // Get current selected level value for highlighting
  const currentPercentage = getPercentage();

  return (
    <div className={`rounded-lg border-2 overflow-hidden transition-colors ${
      isComplete
        ? 'bg-green-50 border-green-200'
        : required
          ? 'bg-white border-slate-200'
          : 'bg-gray-50 border-gray-200'
    }`}>
      {/* Header row - always visible */}
      <button
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
        disabled={disabled}
        className={`w-full flex items-center gap-3 p-3 min-h-[56px] text-left transition-colors ${
          disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-slate-50'
        }`}
      >
        {/* Check circle */}
        <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          isComplete
            ? 'bg-green-500 border-green-500'
            : required
              ? 'border-cyan-400'
              : 'border-gray-300'
        }`}>
          {isComplete && <Check className="h-4 w-4 text-white" />}
        </div>

        {/* Label and value */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Fuel className={`h-4 w-4 ${isComplete ? 'text-green-600' : 'text-slate-400'}`} />
            <p className={`font-medium ${isComplete ? 'text-green-700' : 'text-gray-900'}`}>
              {label}
            </p>
          </div>
          {hasValue && (
            <p className={`text-sm mt-0.5 ${
              getPercentage() <= 25 ? 'text-red-600 font-medium' : 'text-green-600'
            }`}>
              {getDisplayValue()}
              {getPercentage() <= 25 && ' (LOW)'}
            </p>
          )}
        </div>

        {/* Required badge or chevron */}
        {required && !isComplete ? (
          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full flex-shrink-0">
            Required
          </span>
        ) : (
          !disabled && (
            isExpanded
              ? <ChevronUp className="h-5 w-5 text-slate-400" />
              : <ChevronDown className="h-5 w-5 text-slate-400" />
          )
        )}
      </button>

      {/* Expanded content - fuel selection */}
      {isExpanded && !disabled && (
        <div className="px-3 pb-3 border-t border-slate-100">
          {showExact ? (
            /* Exact entry view */
            <div className="mt-3 space-y-3">
              {/* Back button */}
              <button
                onClick={() => setShowExact(false)}
                className="text-sm text-cyan-600 hover:text-cyan-700"
              >
                ← Back to quick select
              </button>

              {/* Unit selector */}
              <div className="grid grid-cols-3 gap-2">
                {EXACT_UNITS.map((unit) => (
                  <button
                    key={unit.value}
                    onClick={() => setExactUnit(unit.value)}
                    className={`
                      py-2 px-3 min-h-[44px] rounded-lg border-2 font-medium text-sm transition-all
                      ${exactUnit === unit.value
                        ? 'bg-cyan-600 border-cyan-700 text-white'
                        : 'bg-white border-slate-200 text-slate-700 hover:border-cyan-300'
                      }
                    `}
                  >
                    {unit.label}
                  </button>
                ))}
              </div>

              {/* Value input with confirm */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={exactValue}
                    onChange={handleExactValueChange}
                    placeholder="Value"
                    className="w-full px-3 py-3 min-h-[48px] text-lg font-bold text-center border-2 border-slate-200 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 outline-none"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {EXACT_UNITS.find(u => u.value === exactUnit)?.suffix}
                  </span>
                </div>
                <button
                  onClick={handleExactConfirm}
                  disabled={!exactValue || isNaN(parseFloat(exactValue))}
                  className="px-4 min-h-[48px] bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                >
                  Set
                </button>
              </div>
            </div>
          ) : (
            /* Quick select view */
            <div className="mt-3 space-y-3">
              {/* 5-button grid */}
              <div className="grid grid-cols-5 gap-2">
                {FUEL_LEVEL_OPTIONS.map((level) => {
                  const isSelected = !value?.isExact && currentPercentage === level.value;
                  return (
                    <button
                      key={level.value}
                      onClick={() => handleQuickSelect(level)}
                      className={`
                        flex flex-col items-center justify-center
                        min-h-[56px] rounded-lg border-2 transition-all
                        ${getLevelColor(level.value, isSelected)} text-white
                      `}
                    >
                      <span className="text-base font-bold">{level.shortLabel}</span>
                      <span className="text-xs opacity-80">{level.value}%</span>
                    </button>
                  );
                })}
              </div>

              {/* Enter exact button */}
              <button
                onClick={() => setShowExact(true)}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors text-sm"
              >
                <Edit3 className="h-4 w-4" />
                <span>Enter Exact</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FuelChecklistItem;
